"use client";

import { useRef } from "react";
import Link from "next/link";
import { motion, Variants } from "framer-motion";
import { ArrowRight, BarChart3, Package, FileText, Bell, Settings, ShoppingBag, Star, RefreshCw, User } from "lucide-react";

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

function VideoShowcase() {
  const videoRef = useRef<HTMLVideoElement>(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
      className="relative w-full max-w-4xl mx-auto mt-12"
    >
      <div className="absolute inset-0 rounded-2xl bg-[#17BC2E]/25 blur-3xl scale-105 pointer-events-none" />
      <div className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-2xl">
        <video
          ref={videoRef}
          src="/ren-power-showcase-compressed.mp4"
          controls
          loop
          playsInline
          className="w-full h-auto"
        />
      </div>
    </motion.div>
  );
}

export default function HeroSection() {
  return (
    <section className="relative min-h-screen bg-[#002301] overflow-hidden flex flex-col">
      {/* SVG background patterns */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-10" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#17BC2E" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* Radial glow spots */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#17BC2E]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FFB600]/8 rounded-full blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        {/* Badge */}
        <motion.div
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6"
        >
          <span className="w-2 h-2 rounded-full bg-[#17BC2E] animate-pulse" />
          <span className="text-white/80 text-sm font-medium">Nigeria's Solar Marketplace</span>
        </motion.div>

        {/* Heading */}
        <motion.h1
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-white font-bold text-[40px] sm:text-[56px] lg:text-[72px] leading-[1.05] tracking-tight max-w-4xl"
        >
          Solar Energy,{" "}
          <span className="text-[#FFB600]">Simplified</span>{" "}
          for Everyone
        </motion.h1>

        {/* Subtext */}
        <motion.p
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-white/70 text-lg sm:text-xl leading-relaxed max-w-2xl mt-6"
        >
          One platform connecting homeowners, manufacturers, distributors, and recyclers across Nigeria.
          Discover products, request quotes, and manage your solar business end to end.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          custom={3}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="flex flex-col sm:flex-row gap-3 mt-8"
        >
          <Link
            href="/marketplace"
            className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-8 py-3.5 rounded-full font-semibold text-base hover:bg-[#17BC2E] hover:text-white hover:scale-105 transition-all duration-300 shadow-lg shadow-[#FFB600]/30"
          >
            Explore Marketplace
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            href="/auth/register"
            className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/30 text-white px-8 py-3.5 rounded-full font-semibold text-base hover:bg-white/20 hover:scale-105 transition-all duration-300 backdrop-blur-sm"
          >
            Get Started Free
          </Link>
        </motion.div>

        {/* Video Showcase - autoplay muted, pauses when out of view */}
        <VideoShowcase />
      </div>
    </section>
  );
}
